export const KONKR_ROLE_LABEL = 'Konkr User';
export const PARTNERSHIPS = ['NotApplied', 'Pending', 'Approved', 'Rejected', 'Suspended'];
export const ADVERTISER_STATUSES = ['Inactive', 'Active'];
export const OFFER_STATUSES = ['Draft', 'Ready', 'Published', 'Expired', 'Disabled'];
export const partnershipLabel = status => status === 'NotApplied' ? 'Not applied' : status;

export const advertiserFields = [
  ['name', 'Name', 250], ['logoUrl', 'Logo URL', 2048], ['websiteUrl', 'Website URL', 2048],
  ['awinAdvertiserId', 'Awin advertiser ID', 100], ['category', 'Category', 100], ['notes', 'Notes', null, 'text'],
];
export const offerFields = [
  ['title', 'Title', 250], ['shortDescription', 'Short description', 1000, 'text'],
  ['fullDescription', 'Full description', null, 'text'], ['voucherCode', 'Voucher code', 200],
  ['landingUrl', 'Landing URL', 2048], ['awinDeeplink', 'Awin deeplink', 2048],
  ['startDate', 'Start date/time', null, 'instant'], ['expiryDate', 'Expiry date/time', null, 'instant'],
  ['activityCategory', 'Activity category', 100], ['region', 'Region', 100], ['terms', 'Terms', null, 'text'],
];
export const fieldsFor = kind => kind === 'advertisers' ? advertiserFields : offerFields;
export const emptyKonkrForm = kind => ({
  ...Object.fromEntries(fieldsFor(kind).map(([key]) => [key, ''])),
  ...(kind === 'advertisers' ? { awinPartnershipStatus: 0 } : { advertiserId: null, pointsRequired: 0, featured: false }),
});
export const konkrForm = (kind, record) => ({
  ...Object.fromEntries(fieldsFor(kind).map(([key]) => [key, record[key] || ''])),
  ...(kind === 'advertisers' ? { awinPartnershipStatus: PARTNERSHIPS.indexOf(record.awinPartnershipStatus) } :
    { advertiserId: record.advertiserId, pointsRequired: record.pointsRequired, featured: record.featured }),
});
export const konkrPayload = (kind, form, record) => ({
  ...Object.fromEntries(fieldsFor(kind).map(([key]) => [key, form[key].trim() || null])),
  ...(kind === 'advertisers' ? { name: form.name.trim(), awinPartnershipStatus: form.awinPartnershipStatus } :
    { advertiserId: form.advertiserId, pointsRequired: form.pointsRequired, featured: form.featured }),
  ...(record ? { revision: record.revision } : {}),
});
export const isHttpUrl = value => {
  try { const url = new URL(value); return ['http:', 'https:'].includes(url.protocol) && Boolean(url.hostname); }
  catch (_) { return false; }
};
export const offsetInstant = value => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,7})?)?(Z|[+-]\d{2}:\d{2})$/i.exec(value || '');
  if (!match) return null;
  const [, year, month, day, hour, minute, second, offset] = match;
  const calendar = new Date(`${year}-${month}-${day}T00:00:00Z`);
  if (!Number(year) || Number(month) < 1 || Number(month) > 12 || Number(day) < 1 ||
      Number.isNaN(calendar.getTime()) || calendar.toISOString().slice(0, 10) !== `${year}-${month}-${day}` ||
      Number(hour) > 23 || Number(minute) > 59 || Number(second || 0) > 59 ||
      (offset.toUpperCase() !== 'Z' && (Number(offset.slice(1, 3)) > 14 || Number(offset.slice(4)) > 59 ||
        (Number(offset.slice(1, 3)) === 14 && Number(offset.slice(4)) !== 0)))) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
};
export const formatKonkrInstant = value => {
  if (!value) return '\u2014';
  const time = offsetInstant(value);
  return time === null ? 'Invalid or offset-free timestamp' :
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', dateStyle: 'medium', timeStyle: 'short' }).format(new Date(time));
};
export const validateKonkr = (kind, form, { publishing = false, advertiser, now = Date.now() } = {}) => {
  const errors = {};
  fieldsFor(kind).forEach(([key, label, max]) => {
    if (max && form[key].length > max) errors[key] = `${label} must be ${max} characters or fewer.`;
  });
  const urls = kind === 'advertisers' ? ['logoUrl', 'websiteUrl'] : ['landingUrl'];
  urls.forEach(key => { if (form[key].trim() && !isHttpUrl(form[key].trim())) errors[key] = 'Enter an absolute HTTP or HTTPS URL.'; });
  if (kind === 'advertisers') {
    if (!form.name.trim()) errors.name = 'Name is required.';
    if (!Number.isInteger(form.awinPartnershipStatus) || !PARTNERSHIPS[form.awinPartnershipStatus]) {
      errors.awinPartnershipStatus = 'Choose an Awin partnership status.';
    }
    return errors;
  }
  if (!Number.isSafeInteger(form.advertiserId) || form.advertiserId <= 0) errors.advertiserId = 'Choose an advertiser.';
  if (!Number.isSafeInteger(form.pointsRequired) || form.pointsRequired < 0) errors.pointsRequired = 'Points must be a nonnegative whole number.';
  if (typeof form.featured !== 'boolean') errors.featured = 'Featured must be Yes or No.';
  ['startDate', 'expiryDate'].forEach(key => {
    if (form[key].trim() && offsetInstant(form[key].trim()) === null) errors[key] = 'Enter an ISO timestamp with Z or an explicit offset, for example 2026-10-20T09:00:00+01:00.';
  });
  const start = offsetInstant(form.startDate.trim()), expiry = offsetInstant(form.expiryDate.trim());
  if (start !== null && expiry !== null && start > expiry) errors.startDate = 'Start must not be after expiry.';
  if (publishing) {
    if (!advertiser || advertiser.id !== form.advertiserId || advertiser.status !== 'Active' || advertiser.awinPartnershipStatus !== 'Approved') {
      errors.advertiserId = 'Publishing requires an Active advertiser with Approved Awin partnership. Backend authorization and current state remain authoritative.';
    }
    if (!form.title.trim()) errors.title = 'Title is required for publishing.';
    if (!isHttpUrl(form.awinDeeplink.trim())) errors.awinDeeplink = 'Publishing requires an absolute HTTP or HTTPS Awin deeplink.';
    if (expiry === null || expiry <= now) errors.expiryDate = 'Publishing requires an expiry in the future.';
    if (!form.region.trim()) errors.region = 'Region is required for publishing.';
  }
  return errors;
};
export const konkrError = (error, fallback) => {
  const status = error.response && error.response.status;
  if (status === 409) return 'This record changed or the action conflicts with its current state. Reload before retrying; your unsaved values have been preserved.';
  if (status === 404) return 'This record is no longer available. Close the editor and refresh the list.';
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'Konkr access was denied. Your role may have been revoked; sign in again or contact an administrator.';
  const data = error.response && error.response.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && data.errors) return Object.values(data.errors).flat().join(' ') || fallback;
  if (data && (data.error || data.detail || data.title)) return data.error || data.detail || data.title;
  return error.message || fallback;
};
export const konkrFieldErrors = error => {
  const errors = error.response && error.response.data && error.response.data.errors;
  return errors && typeof errors === 'object' ? Object.fromEntries(Object.entries(errors).map(([key, value]) => {
    const field = key.replace(/^\$\./, '');
    return [field.charAt(0).toLowerCase() + field.slice(1), [].concat(value).join(' ')];
  })) : {};
};
